import { useNavigate } from "react-router-dom";
import ProjectCardLink from "./ProjectCardLink";
import { ArrowUpRightIcon } from "../../components/Icons";
import { croppedImageUrl } from "../../data/sanity/client";
import Emphasis from "../../components/Emphasis";

const GlobeIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

const GitHubIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z" />
  </svg>
);

function ProjectCard({ slug, title, description, tools, banner, liveUrl, sourceUrl }) {
  const navigate = useNavigate();
  // The card is a fixed 16/10 box, so Sanity does the crop rather than CSS —
  // that way it happens around the focal point set in the Studio. 1000px is 2x
  // the ~490px the card occupies in the two-up grid.
  const bannerSrc = croppedImageUrl(banner, 1000, 625);

  return (
    <div
      onClick={() => {
        sessionStorage.setItem("workScrollY", window.scrollY);
        navigate(`/work/${slug}`);
      }}
      className="group h-full rounded-2xl cursor-pointer hover:shadow-xl dark:hover:shadow-[0_8px_30px_rgba(255,255,255,0.05)] transition-all duration-300"
    >
      <div className="h-full rounded-2xl border border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark overflow-hidden flex flex-col">

        {/* Upper: full-bleed image with floating links */}
        <div className="relative aspect-[16/10] bg-neutral-100 dark:bg-neutral-900">
          {bannerSrc && (
            <img
              src={bannerSrc}
              alt={banner?.alt || title}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          )}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            {liveUrl   && <ProjectCardLink href={liveUrl}   label="Preview" icon={<GlobeIcon />} glass tooltip="Live preview" />}
            {sourceUrl && <ProjectCardLink href={sourceUrl} label="Github"  icon={<GitHubIcon />} glass />}
          </div>
        </div>

        {/* Lower: title, description, stack */}
        <div className="p-4 flex flex-col flex-1">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-sans font-semibold text-base text-text-primary-light dark:text-text-primary-dark transition-colors duration-300 group-hover:text-brand-blue">
              <Emphasis>{title}</Emphasis>
            </h3>
            <span className="text-text-muted-light dark:text-text-muted-dark mt-0.5 ml-2 flex-shrink-0"><ArrowUpRightIcon size={14} /></span>
          </div>

          <p className="font-sans text-sm text-text-muted-light dark:text-text-muted-dark leading-relaxed mb-4 line-clamp-2 flex-1">
            {description}
          </p>

          <div className="flex flex-wrap gap-1.5">
            {(tools || []).map((tech) => (
              <span
                key={tech}
                className="px-2.5 py-1 rounded-full border border-border-light dark:border-border-dark font-sans text-2xs text-text-muted-light dark:text-text-muted-dark"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

export default ProjectCard;
